import { useEffect, useRef } from "react";

const TAWK_SRC = "https://embed.tawk.to/6982eed13c76db1c37e39c87/1jgjn9p4d";
const TAWK_SCRIPT_ID = "tawk-embed-script";

export function useTawkWidget({ chatBtnRef, debug = false }) {
    const isOpenRef = useRef(false);
    const pendingOpenRef = useRef(false);
    const loadingRef = useRef(false);
    const observerRef = useRef(null);
    const closingLockRef = useRef(false);

    const ignoreBlurUntilRef = useRef(0);
    const lastPointerDownAtRef = useRef(0);

    const log = (...args) => debug;

    const setBodyOpen = (open) => {
        document.body.classList.toggle("tawk-open", !!open);
        log("setBodyOpen:", !!open);
    };

    const purgeFloatingWidgets = () => {
        const H = window.innerHeight;
        const W = window.innerWidth;

        const nodes = Array.from(document.body.querySelectorAll("*"));
        let removed = 0;

        nodes.forEach((el) => {
            if (el.closest("#root")) return;
            if (el.tagName === "SCRIPT") return;
            if (chatBtnRef.current && chatBtnRef.current.contains(el)) return;

            const cs = window.getComputedStyle(el);
            if (cs.position !== "fixed") return;

            const rect = el.getBoundingClientRect();

            const nearBottom = rect.bottom > H - 320 && rect.height > 10 && rect.width > 10;
            const nearLeft = rect.left < 320;
            const nearRight = rect.right > W - 320;

            if (!nearBottom || (!nearLeft && !nearRight)) return;

            const sizeOk = rect.width <= 420 && rect.height <= 420;
            if (!sizeOk) return;

            try {
                el.remove();
                removed++;
            } catch (e) { }
        });

        if (removed) log("purgeFloatingWidgets removed:", removed);
    };

    const hideKnownTawkIds = () => {
        const idsToHide = [
            "tawkchat-attention-grabber",
            "tawkchat-call-to-action",
            "tawkchat-status-message",
            "tawkchat-status-text",
            "tawkchat-minified-wrapper",
            "tawkchat-bubble",
            "tawkchat-launcher",
        ];

        idsToHide.forEach((id) => {
            const el = document.getElementById(id);
            if (!el) return;
            el.style.setProperty("display", "none", "important");
            el.style.setProperty("visibility", "hidden", "important");
            el.style.setProperty("opacity", "0", "important");
            el.style.setProperty("pointer-events", "none", "important");
        });
    };

    const enforceUi = () => {
        hideKnownTawkIds();
        if (!isOpenRef.current) purgeFloatingWidgets();
    };

    const lateEnforce = () => {
        setTimeout(enforceUi, 50);
        setTimeout(enforceUi, 200);
        setTimeout(enforceUi, 600);
        setTimeout(enforceUi, 1200);
        setTimeout(enforceUi, 2000);
    };

    const stopObserver = () => {
        observerRef.current?.disconnect?.();
        observerRef.current = null;
        log("Observer stopped");
    };

    const startObserver = () => {
        if (observerRef.current) return;

        const obs = new MutationObserver(() => {
            enforceUi();
        });

        obs.observe(document.body, { childList: true, subtree: true });
        observerRef.current = obs;

        log("Observer started");
        enforceUi();
    };

    const closeTawk = (reason = "manual") => {
        if (closingLockRef.current) return;
        closingLockRef.current = true;

        log("closeTawk()", { reason });

        isOpenRef.current = false;
        setBodyOpen(false);

        try {
            window.Tawk_API?.minimize?.();
            window.Tawk_API?.hideWidget?.();
        } catch (e) { }

        detachOutside();
        enforceUi();
        lateEnforce();

        setTimeout(() => {
            closingLockRef.current = false;
        }, 120);
    };

    const onEsc = (e) => {
        if (e.key === "Escape") closeTawk("esc");
    };

    const onOutside = (e) => {
        if (!isOpenRef.current) return;
        if (chatBtnRef.current?.contains(e.target)) return;
        closeTawk("outside");
    };

    const onPointerDownCapture = () => {
        lastPointerDownAtRef.current = Date.now();
    };

    const onWindowBlur = () => {
        if (!isOpenRef.current) return;

        const now = Date.now();
        if (now < ignoreBlurUntilRef.current) return;

        const dt = now - lastPointerDownAtRef.current;
        if (dt > 800) return;

        setTimeout(() => closeTawk("iframe-blur"), 0);
    };

    const detachOutside = () => {
        document.removeEventListener("mousedown", onOutside, true);
        document.removeEventListener("touchstart", onOutside, true);
        document.removeEventListener("keydown", onEsc, true);

        document.removeEventListener("mousedown", onPointerDownCapture, true);
        document.removeEventListener("touchstart", onPointerDownCapture, true);
        window.removeEventListener("blur", onWindowBlur, true);

        log("detachOutside");
    };

    const attachOutside = () => {
        setTimeout(() => {
            document.addEventListener("mousedown", onOutside, true);
            document.addEventListener("touchstart", onOutside, true);
            document.addEventListener("keydown", onEsc, true);

            document.addEventListener("mousedown", onPointerDownCapture, true);
            document.addEventListener("touchstart", onPointerDownCapture, true);
            window.addEventListener("blur", onWindowBlur, true);

            log("attachOutside");
        }, 0);
    };

    const openNow = (reason = "manual") => {
        if (!window.Tawk_API?.maximize) {
            log("openNow: not ready");
            return;
        }

        log("openNow()", { reason });

        ignoreBlurUntilRef.current = Date.now() + 900;

        isOpenRef.current = true;
        setBodyOpen(true);

        try {
            window.Tawk_API.showWidget?.();
            window.Tawk_API.maximize?.();
        } catch (e) { }

        enforceUi();
        attachOutside();
    };

    const bindTawkEvents = () => {
        if (!window.Tawk_API) return;

        window.Tawk_API.onChatMinimized = () => closeTawk("onChatMinimized");
        window.Tawk_API.onChatHidden = () => closeTawk("onChatHidden");
        window.Tawk_API.onChatEnded = () => closeTawk("onChatEnded");

        window.Tawk_API.onChatMaximized = () => {
            ignoreBlurUntilRef.current = Date.now() + 900;
            isOpenRef.current = true;
            setBodyOpen(true);
            enforceUi();
            attachOutside();
        };

        log("Tawk events bound");
    };

    const ensureTawkLoaded = () => {
        startObserver();

        if (window.Tawk_API?.maximize) {
            log("Tawk already ready");
            bindTawkEvents();
            return;
        }

        if (document.getElementById(TAWK_SCRIPT_ID)) {
            log("Tawk script already in DOM, waiting onLoad");
            return;
        }

        if (loadingRef.current) return;
        loadingRef.current = true;

        window.Tawk_API = window.Tawk_API || {};
        window.Tawk_LoadStart = new Date();

        window.Tawk_API.customStyle = {
            visibility: {
                desktop: { position: "bl" },
                mobile: { position: "bl" },
            },
        };

        window.Tawk_API.onLoad = function () {
            loadingRef.current = false;

            try {
                window.Tawk_API.hideWidget?.();
            } catch (e) { }

            isOpenRef.current = false;
            setBodyOpen(false);

            enforceUi();
            lateEnforce();

            bindTawkEvents();

            if (pendingOpenRef.current) {
                pendingOpenRef.current = false;
                openNow("pending");
            }
        };

        const s1 = document.createElement("script");
        s1.id = TAWK_SCRIPT_ID;
        s1.async = true;
        s1.src = TAWK_SRC;
        s1.charset = "UTF-8";
        s1.setAttribute("crossorigin", "*");
        document.body.appendChild(s1);

        log("Tawk script appended:", TAWK_SRC);
    };

    const toggleTawk = () => {
        log("toggleTawk()", { isOpen: isOpenRef.current });

        if (isOpenRef.current) {
            closeTawk("toggle");
            return;
        }

        ensureTawkLoaded();

        if (window.Tawk_API?.maximize) {
            openNow("toggle");
        } else {
            pendingOpenRef.current = true;
        }
    };

    useEffect(() => {
        log("Home mounted");

        isOpenRef.current = false;
        setBodyOpen(false);

        try {
            window.Tawk_API?.hideWidget?.();
        } catch (e) { }

        startObserver();
        enforceUi();
        lateEnforce();

        return () => {
            closeTawk("unmount");
            stopObserver();
        }; s
    }, []);

    return { toggleTawk };
}
